import type { AuctionResult, CommissionType, DividendTiming } from "../services/types";
import { formatINR, formatINRNumber } from "./format";

/**
 * Client-side auction arithmetic, used for the LIVE PREVIEW and bid validation
 * during negotiation. These are DISPLAY-ONLY numbers — the authoritative result
 * is computed by the backend when the auction is closed (computeAuctionResult
 * mirrors it here for the closed view, but the close action sends the winning
 * bid to the API and renders what it returns). Never treat the preview as the
 * stored record.
 */

export interface LivePreview {
  discount: number;
  discountPct: number;
  commission: number;
  commissionPerMember: number;
  dividendPool: number;
  perMemberDividend: number;
  dividendRemainder: number;
  netInstallment: number;
  totalCollected: number;
}

export function computeCommission(
  commissionType: CommissionType = "none",
  commissionValue: number = 0,
  pool: number = 0,
  discount: number = 0
): number {
  let raw = 0;
  switch (commissionType) {
    case "none":
      raw = 0;
      break;
    case "pool_percentage":
      // Commission of gross pool after bidding (actual pool price - discount)
      raw = Math.round(Math.max(0, pool - discount) * commissionValue) / 100;
      break;
    case "discount_percentage":
      raw = Math.floor((discount * commissionValue) / 100);
      break;
    case "fixed":
      raw = commissionValue;
      break;
  }
  return Math.max(0, raw);
}

/** Live feedback shown beside the amount field as staff type a bid. */
export function computeLivePreview(input: {
  pool: number;
  monthlyInstallment: number;
  memberCount: number;
  dividendTiming: DividendTiming;
  amount: number;
  commissionType?: CommissionType;
  commissionValue?: number;
}): LivePreview {
  const discount = Math.max(0, input.pool - input.amount);
  const discountPct = input.pool > 0 ? (discount / input.pool) * 100 : 0;
  const dividendPool = discount;
  const perMemberDividend = dividendPool > 0 && input.memberCount > 0 ? Math.floor(dividendPool / input.memberCount) : 0;
  const dividendRemainder = dividendPool - perMemberDividend * input.memberCount;
  const commission = computeCommission(
    input.commissionType,
    input.commissionValue,
    input.pool,
    discount
  );
  const commissionPerMember = input.memberCount > 0 ? Math.floor((commission * 100) / input.memberCount) / 100 : 0;
  const totalCycleCommission = Math.round(commissionPerMember * input.memberCount * 100) / 100;
  // Discount subtracts as dividend, commission adds as commission share
  const netInstallment =
    (input.dividendTiming === "next_month"
      ? input.monthlyInstallment
      : input.monthlyInstallment - perMemberDividend) + commissionPerMember;
  const totalCollected = netInstallment * input.memberCount;
  return {
    discount,
    discountPct,
    commission: totalCycleCommission,
    commissionPerMember,
    dividendPool,
    perMemberDividend,
    dividendRemainder,
    netInstallment,
    totalCollected,
  };
}

/** Build a bid's validation error message, or null when the bid is acceptable. */
export function validateBid(input: {
  amount: number;
  pool: number;
  currentLowest: number | null;
  bidStep: number;
  minDiscountPct?: number | null;
  maxDiscountPct?: number | null;
}): string | null {
  const { amount, pool, currentLowest, bidStep, minDiscountPct, maxDiscountPct } = input;
  if (!Number.isFinite(amount) || amount <= 0) {
    return "Enter an amount greater than zero.";
  }
  if (amount > pool) {
    return "The amount cannot exceed the pool.";
  }
  if (currentLowest !== null && amount >= currentLowest) {
    return `The bid must be lower than the current lowest bid of ${formatINR(currentLowest)}.`;
  }
  if (currentLowest !== null && currentLowest - amount < bidStep) {
    return `The bid must be lower by at least the configured bid step of ${formatINR(bidStep)}.`;
  }
  const discount = pool - amount;
  if (bidStep > 0 && discount % bidStep !== 0) {
    return `Bids must follow the configured step of ${formatINR(bidStep)} (discount ${formatINR(discount)} is not on step).`;
  }
  if (currentLowest !== null && bidStep > 0 && (currentLowest - amount) % bidStep !== 0) {
    return `Bid must raise by an exact multiple of the configured bid step of ${formatINR(bidStep)}.`;
  }
  if (minDiscountPct != null && discount < pool * (minDiscountPct / 100)) {
    return `The resulting discount is below the configured minimum of ${minDiscountPct}%.`;
  }
  if (maxDiscountPct != null && discount > pool * (maxDiscountPct / 100)) {
    return `The resulting discount exceeds the configured maximum of ${maxDiscountPct}%.`;
  }
  return null;
}

/** Build the authoritative result for a decided cycle. Mirrors the backend calc. */
export function computeAuctionResult(input: {
  pool: number;
  monthlyInstallment: number;
  memberCount: number;
  dividendTiming: DividendTiming;
  winnerMemberId: string;
  winningBid: number;
  winnerPendingDues: number;
  closedBy: string;
  closedAt: string;
  commissionType?: CommissionType;
  commissionValue?: number;
}): AuctionResult {
  const discount = Math.max(0, input.pool - input.winningBid);
  const dividendPool = discount;
  const perMemberDividend = dividendPool > 0 && input.memberCount > 0 ? Math.floor(dividendPool / input.memberCount) : 0;
  const dividendRemainder = dividendPool - perMemberDividend * input.memberCount;
  const baseInstallmentAfterDividend = Math.max(0, input.monthlyInstallment - perMemberDividend);
  const commission = computeCommission(
    input.commissionType,
    input.commissionValue,
    input.pool,
    discount,
    baseInstallmentAfterDividend,
    input.memberCount
  );
  const commissionPerMember = input.memberCount > 0 ? Math.floor((commission * 100) / input.memberCount) / 100 : 0;
  const totalCycleCommission = Math.round(commissionPerMember * input.memberCount * 100) / 100;
  // Discount subtracts as dividend, commission adds as commission share
  const netInstallment =
    (input.dividendTiming === "next_month"
      ? input.monthlyInstallment
      : input.monthlyInstallment - perMemberDividend) + commissionPerMember;
  const totalCollected = netInstallment * input.memberCount;
  return {
    winnerMemberId: input.winnerMemberId,
    winningBid: input.winningBid,
    discount,
    commission: totalCycleCommission,
    commissionPerMember,
    perMemberDividend,
    dividendRemainder,
    netInstallment,
    totalCollected,
    winnerPendingDues: input.winnerPendingDues,
    winnerPayout: Math.max(0, input.winningBid - input.winnerPendingDues),
    closedAt: input.closedAt,
    closedBy: input.closedBy,
  };
}

/** True when two ISO dates fall on the same calendar day. */
export function isSameDay(a: string | Date, b: string | Date): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

/** Compact discount-percentage helper for labels. */
export function discountPercentLabel(discount: number, pool: number): string {
  if (pool <= 0) return "0%";
  return `${formatINRNumber((discount / pool) * 100)}%`;
}